import {
  EmailAuthProvider,
  type User,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { type ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import type { UserRole } from '../constants/roles';
import { authClient } from '../firebase/init';

// ============================================================
// Lume / TEACare PWA — AuthContext
// ============================================================
// Custom claims expostos:
//   - role         : UserRole atribuída ao usuário
//   - organizationId: id da organização SUS associada (opcional)
//   - mfaVerified  : flag de MFA aprovada na sessão atual (opcional)
// ============================================================

export interface AuthClaims {
  role?: UserRole;
  organizationId?: string;
  mfaVerified?: boolean;
}

export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  claims: AuthClaims;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  reauthForMFA: (email: string, password: string) => Promise<void>;
  refreshClaims: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================
// E2E test harness — DEV-only short-circuit
// ============================================================
// Quando rodando local/CI E2E sob Playwright, podemos injetar um
// stub de auth via window.__LUME_TEST_AUTH__ para destravar
// jornadas que exigem usuário autenticado SEM precisar do Firebase
// Auth Emulator. NUNCA é ativada em produção (gate por
// import.meta.env.DEV + presença explícita da flag).
//
// Convention de payload (definida pelo Playwright via addInitScript):
//   window.__LUME_TEST_AUTH__ = {
//     uid: 'test-uid',
//     email: 'teste@lume.local',
//     displayName: 'Teste',
//     claims: { role: 'physician', mfaVerified: true, organizationId: 'org-1' }
//   }
// ============================================================
interface TestAuthPayload {
  uid: string;
  email?: string;
  displayName?: string;
  claims: AuthClaims;
}
declare global {
  interface Window {
    __LUME_TEST_AUTH__?: TestAuthPayload;
  }
}

function readTestAuthPayload(): TestAuthPayload | null {
  if (typeof window === 'undefined') return null;
  // Vite expose `import.meta.env.DEV` em build. Evita vazar para prod
  // mesmo se algum atacante setasse window.__LUME_TEST_AUTH__.
  let isDev = false;
  try {
    isDev = Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
  } catch {
    isDev = false;
  }
  if (!isDev) return null;
  return window.__LUME_TEST_AUTH__ ?? null;
}

function extractClaims(raw: Record<string, unknown> | undefined): AuthClaims {
  if (!raw) return {};
  return {
    role: typeof raw.role === 'string' ? (raw.role as UserRole) : undefined,
    organizationId: typeof raw.organizationId === 'string' ? raw.organizationId : undefined,
    mfaVerified: typeof raw.mfaVerified === 'boolean' ? raw.mfaVerified : undefined,
  };
}

export interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const testPayload = readTestAuthPayload();
  const [user, setUser] = useState<User | null>(() =>
    testPayload
      ? // Cast minimal — exposed surface usada pelo app é uid/email/displayName.
        ({
          uid: testPayload.uid,
          email: testPayload.email ?? null,
          displayName: testPayload.displayName ?? null,
          isAnonymous: false,
          providerId: 'test',
        } as unknown as User)
      : null,
  );
  const [loading, setLoading] = useState<boolean>(testPayload ? false : true);
  const [claims, setClaims] = useState<AuthClaims>(() =>
    testPayload ? testPayload.claims : {},
  );

  const refreshClaims = useCallback(async (): Promise<void> => {
    if (testPayload) {
      setClaims(testPayload.claims);
      return;
    }
    const current = authClient().currentUser;
    if (!current) {
      setClaims({});
      return;
    }
    const tokenResult = await current.getIdTokenResult(true);
    setClaims(extractClaims(tokenResult.claims as Record<string, unknown>));
  }, [testPayload]);

  useEffect(() => {
    if (testPayload) return; // short-circuit em E2E DEV-only
    const unsubscribe = onAuthStateChanged(authClient(), async (next) => {
      setUser(next);
      if (next) {
        const tokenResult = await next.getIdTokenResult();
        setClaims(extractClaims(tokenResult.claims as Record<string, unknown>));
      } else {
        setClaims({});
      }
      setLoading(false);
    });
    return unsubscribe;
  }, [testPayload]);

  const signIn = useCallback(async (email: string, password: string): Promise<void> => {
    if (testPayload) return; // E2E stub: não toca Firebase
    await signInWithEmailAndPassword(authClient(), email, password);
  }, [testPayload]);

  const signOut = useCallback(async (): Promise<void> => {
    if (testPayload) {
      setUser(null);
      setClaims({});
      return;
    }
    await firebaseSignOut(authClient());
  }, [testPayload]);

  const reauthForMFA = useCallback(async (email: string, password: string): Promise<void> => {
    const current = authClient().currentUser;
    if (!current) throw new Error('Sem sessão ativa para reautenticar.');
    const credential = EmailAuthProvider.credential(email, password);
    await reauthenticateWithCredential(current, credential);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, claims, signIn, signOut, reauthForMFA, refreshClaims }),
    [user, loading, claims, signIn, signOut, reauthForMFA, refreshClaims],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
